import { useEffect, useState, type FormEvent } from 'react'
import { AleloButton, AleloLayout } from '@/components/AleloLayout'
import { AleloStepper, stepperTotal } from '@/components/AleloStepper'
import styles from '@/components/AleloLayout.module.css'
import { RangoInputCode, RangoLink } from '@/components/rango'
import { maskPhone } from '@/utils/mask'
import * as signupDraft from '@/utils/signupDraft'
import { toTransactionErrorCode, type TransactionErrorLike } from '@/utils/transactionError'
import { RESEND_SECONDS } from '@/screens/signup-shared/useSignupVerification'
import locales from './locales/pt-BR.json'

const CODE_LENGTH = 6
/** Passo "Confirmar celular" do cadastro: Dados → e-mail → celular → senha. */
const SIGNUP_STEP = 3

type PhoneChallengeSdk = {
  screen?: { data?: { phone?: string } | null }
  submitPhoneChallenge: (payload: { code: string }) => Promise<void>
  resendCode: () => Promise<void>
  returnToPrevious: () => Promise<void>
  getErrors?: () => TransactionErrorLike[]
}

async function createSdk(): Promise<PhoneChallengeSdk | null> {
  try {
    const mod = await import('@auth0/auth0-acul-js/phone-identifier-challenge')
    return new mod.default() as unknown as PhoneChallengeSdk
  } catch {
    return null
  }
}

const fmt = (text: string, values: Record<string, string | number>) =>
  text.replace(/\{(\w+)\}/g, (_, key: string) => String(values[key] ?? ''))

/**
 * Verificação do celular no cadastro (`phone-identifier-challenge`), feita
 * pela Auth0 depois do passo 3 da `signup-id`. Só SMS. "Voltar e corrigir o
 * número" usa o `returnToPrevious` do SDK e o marcador do rascunho, para a
 * `signup-id` reabrir no celular com os dados preenchidos.
 */
export default function PhoneIdentifierChallengeScreen() {
  const [sdk, setSdk] = useState<PhoneChallengeSdk | null>(null)
  const [code, setCode] = useState('')
  const [error, setError] = useState<string>()
  const [pending, setPending] = useState(false)
  // O código acabou de ser enviado quando a tela abre.
  const [resendSeconds, setResendSeconds] = useState(RESEND_SECONDS)

  useEffect(() => {
    let cancelled = false
    void createSdk().then((instance) => {
      if (!cancelled) setSdk(instance)
    })
    return () => {
      cancelled = true
    }
  }, [])

  useEffect(() => {
    if (resendSeconds <= 0) return
    const timer = window.setTimeout(() => setResendSeconds(resendSeconds - 1), 1000)
    return () => window.clearTimeout(timer)
  }, [resendSeconds])

  // Código errado volta no re-render da Auth0, como erro da transação.
  const rejected = toTransactionErrorCode(sdk?.getErrors?.()) ? locales.rejected : undefined
  const phone = sdk?.screen?.data?.phone
  const sentTo = phone ? fmt(locales.sentTo, { phone: maskPhone(phone) }) : locales.sentToFallback

  async function onSubmit(event: FormEvent) {
    event.preventDefault()
    setError(undefined)
    if (!new RegExp(`^\\d{${CODE_LENGTH}}$`).test(code)) {
      setError(locales.invalidCode)
      return
    }
    setPending(true)
    try {
      if (sdk) await sdk.submitPhoneChallenge({ code })
      else console.info('[preview phone-identifier-challenge]', { code })
    } finally {
      setPending(false)
    }
  }

  async function onResend() {
    if (resendSeconds > 0 || pending) return
    setError(undefined)
    setResendSeconds(RESEND_SECONDS)
    if (sdk) await sdk.resendCode()
    else console.info('[preview phone-identifier-challenge] resend')
  }

  async function onBack() {
    signupDraft.markReturn()
    if (sdk) await sdk.returnToPrevious()
    else window.location.search = '?screen=signup-id'
  }

  return (
    <AleloLayout>
      <AleloStepper currentStep={SIGNUP_STEP} withPassword />
      <p className={styles.signupHint}>
        {`Passo ${SIGNUP_STEP} de ${stepperTotal(undefined, true)}`}
      </p>
      <h1 className={styles.title}>{locales.title}</h1>
      <p className={styles.subtitle}>{sentTo}</p>
      <form className={styles.stack} onSubmit={(event) => void onSubmit(event)} noValidate>
        <RangoInputCode
          required
          label={locales.codeLabel}
          value={code}
          onChange={setCode}
          length={CODE_LENGTH}
          supportText={locales.codeSupport}
          error={error ?? rejected}
          disabled={pending}
          autoComplete="one-time-code"
          autoFocus
        />
        <AleloButton type="submit" disabled={pending} loading={pending}>
          {locales.submit}
        </AleloButton>
        <div className={styles.center}>
          <RangoLink
            onClick={() => void onResend()}
            disabled={resendSeconds > 0 || pending}
            aria-disabled={resendSeconds > 0 || pending}
          >
            {resendSeconds > 0
              ? fmt(locales.resendIn, { n: resendSeconds })
              : locales.resend}
          </RangoLink>
        </div>
        <div className={styles.center}>
          <AleloButton variant="link" onClick={() => void onBack()} disabled={pending}>
            {locales.back}
          </AleloButton>
        </div>
      </form>
    </AleloLayout>
  )
}
